import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableContainer
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';

import { ContentWrapper } from '@/components/ContentWrapper';
import {
    FONT_WEIGHT,
    Heading,
    LINE_HEIGHT,
    PageSubHeading,
    SMBold,
    SMRegular
} from '@/typography/Typography';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { PrimaryButton } from '@/components/shared/Button';
import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from '@/components/shared/styles/Table';
import { useUser } from '@/contexts/user/UserContext';
import { UserRoles } from '@/models/company';
import { useAdminCompanies, useAppDispatch } from '@/lib/hooks';
import { getAdminCompanies } from '@/lib/slices/paymentsSlice';
import { DATE_FORMAT } from '@/lib/constants';
import { useUserCommunicationHistoryQuery } from '@/services/reportsQuery';
import {
    USER_LOOKUP_ERROR_CODE,
    UserCommunicationTimelineEntry,
    UserLookupCandidate,
    UserLookupErrorResponse
} from '@/services/UserCommunicationHistoryTypes';

const FilterBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.greySurface,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greyBorder}`,
    margin: theme.spacing(1)
}));

// Accepts the canonical 8-4-4-4-12 hex GUID; trims so a pasted id with stray
// whitespace still validates before we enable the (otherwise wasted) request.
const GUID_REGEX =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// Client-side gate only: this mirrors the server's "contains @ => email" split so we
// don't fire an obviously-malformed request. Authoritative resolution is server-side.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidIdentifier = (value: string) =>
    GUID_REGEX.test(value) || EMAIL_REGEX.test(value);

// Pulls the stable { code, candidates? } envelope off a failed lookup. The backend
// returns it for 400/404/409; anything else (network, 5xx) yields null so the page
// falls back to the generic error copy. Never reads message text — behaviour keys
// off `code` only (US-3385).
const extractLookupError = (error: unknown): UserLookupErrorResponse | null => {
    const data = (error as AxiosError<UserLookupErrorResponse>)?.response?.data;
    if (data?.code) return data;
    return null;
};

const COMMUNICATION_KIND: UserCommunicationTimelineEntry['kind'] =
    'Communication';

export const UserCommunicationHistoryReport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useUser();
    const { companies } = useAdminCompanies();

    const isSuperAdmin = user?.role === UserRoles.SuperAdmin;

    useEffect(() => {
        if (!isSuperAdmin) {
            navigate('/', { replace: true });
        }
    }, [isSuperAdmin, navigate]);

    useEffect(() => {
        dispatch(getAdminCompanies());
    }, [dispatch]);

    const [companyId, setCompanyId] = useState<string>('');
    const [identifierInput, setIdentifierInput] = useState<string>('');
    // The query key the user actually asked for — committed on Run so typing
    // doesn't refire the request on every keystroke.
    const [submitted, setSubmitted] = useState<{
        companyId: string;
        identifier: string;
    } | null>(null);

    const companyChoices = useMemo(
        () =>
            (companies ?? [])
                .map((c) => ({ label: c.name, value: c.companyId }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [companies]
    );

    const trimmedIdentifier = identifierInput.trim();
    const isIdentifierValid = isValidIdentifier(trimmedIdentifier);
    const canRun = !!companyId && isIdentifierValid;

    const { data, isFetching, isError, error } =
        useUserCommunicationHistoryQuery(
            submitted?.companyId ?? '',
            submitted?.identifier ?? '',
            submitted !== null
        );

    const handleRun = () => {
        if (!canRun) return;
        setSubmitted({ companyId, identifier: trimmedIdentifier });
    };

    if (!isSuperAdmin) return null;

    const lookupError = extractLookupError(error);
    const timeline = data?.timeline ?? [];

    return (
        <ContentWrapper>
            <>
                <Box m={1}>
                    <Heading
                        fontSize={20}
                        fontWeight={FONT_WEIGHT.MEDIUM}
                        lineHeight={LINE_HEIGHT.XLARGE}
                    >
                        {t('user_communication_history.title')}
                    </Heading>
                    <PageSubHeading>
                        {t('user_communication_history.subtitle')}
                    </PageSubHeading>
                </Box>

                <FilterBox>
                    <Box minWidth={240}>
                        <SMBold>
                            {t('user_communication_history.company')}
                        </SMBold>
                        <Dropdown
                            label={t(
                                'user_communication_history.select_company'
                            )}
                            choices={companyChoices}
                            value={companyId || null}
                            onChange={(value: string) =>
                                setCompanyId(value ?? '')
                            }
                            allowClear
                        />
                    </Box>
                    <Box minWidth={320}>
                        <InputText
                            label={t('user_communication_history.identifier')}
                            placeholder={t(
                                'user_communication_history.identifier_placeholder'
                            )}
                            value={identifierInput}
                            onChange={(e) => setIdentifierInput(e.target.value)}
                            error={!!trimmedIdentifier && !isIdentifierValid}
                            helperText={
                                !!trimmedIdentifier && !isIdentifierValid
                                    ? t(
                                          'user_communication_history.identifier_invalid'
                                      )
                                    : undefined
                            }
                        />
                    </Box>
                    <Box pb={'3px'}>
                        <PrimaryButton disabled={!canRun} onClick={handleRun}>
                            {t('user_communication_history.run')}
                        </PrimaryButton>
                    </Box>
                </FilterBox>

                {submitted && (
                    <TableCard>
                        <CardHeader
                            label={
                                data
                                    ? data.displayIdentity
                                    : t('user_communication_history.title')
                            }
                            description={t(
                                'user_communication_history.results_for_user'
                            )}
                            chip={
                                data?.isDeletedUser ? (
                                    <Chip
                                        color="muted"
                                        label={t(
                                            'user_communication_history.deleted_user'
                                        )}
                                    />
                                ) : undefined
                            }
                        />

                        {isFetching && (
                            <Box display="flex" justifyContent="center" py={6}>
                                <CircularProgress size={32} />
                            </Box>
                        )}

                        {!isFetching && isError && (
                            <Box px={2} pb={2}>
                                <LookupErrorAlert error={lookupError} />
                            </Box>
                        )}

                        {!isFetching && !isError && timeline.length === 0 && (
                            <Box px={2} py={4}>
                                <SMRegular color="text.secondary">
                                    {t('user_communication_history.no_events')}
                                </SMRegular>
                            </Box>
                        )}

                        {!isFetching && !isError && timeline.length > 0 && (
                            <TableContainer>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell>
                                                {t(
                                                    'user_communication_history.col_when'
                                                )}
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                {t(
                                                    'user_communication_history.col_type'
                                                )}
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                {t(
                                                    'user_communication_history.col_channel'
                                                )}
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                {t(
                                                    'user_communication_history.col_event'
                                                )}
                                            </TableHeaderCell>
                                            <TableHeaderCell>
                                                {t(
                                                    'user_communication_history.col_detail'
                                                )}
                                            </TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {timeline.map((entry, index) => (
                                            <TimelineRow
                                                key={`${entry.occurredAt}-${index}`}
                                                entry={entry}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </TableCard>
                )}
            </>
        </ContentWrapper>
    );
};

interface TimelineRowProps {
    entry: UserCommunicationTimelineEntry;
}

const TimelineRow = ({ entry }: TimelineRowProps) => {
    const { t } = useTranslation();
    const isCommunication = entry.kind === COMMUNICATION_KIND;

    const detail =
        entry.campaignName || entry.templateId || entry.clickedUrl || '—';

    return (
        <TableRow>
            <TableCell>
                <SMRegular>
                    {dayjs(entry.occurredAt).format(DATE_FORMAT.FULL_DATE_TIME)}
                </SMRegular>
            </TableCell>
            <TableCell>
                <Chip
                    color={isCommunication ? 'info' : 'success'}
                    label={
                        isCommunication
                            ? t('user_communication_history.kind_communication')
                            : t('user_communication_history.kind_activity')
                    }
                />
            </TableCell>
            <TableCell>
                <SMRegular>{entry.channel ?? '—'}</SMRegular>
            </TableCell>
            <TableCell>
                <SMRegular>{entry.eventType}</SMRegular>
            </TableCell>
            <TableCell>
                <SMRegular>{detail}</SMRegular>
            </TableCell>
        </TableRow>
    );
};

interface LookupErrorAlertProps {
    error: UserLookupErrorResponse | null;
}

// Distinguishes the backend's stable error codes so the operator sees a message
// that matches why the lookup failed, rather than one collapsed "try again" alert.
const LookupErrorAlert = ({ error }: LookupErrorAlertProps) => {
    const { t } = useTranslation();

    if (error?.code === USER_LOOKUP_ERROR_CODE.AMBIGUOUS) {
        return (
            <Alert severity="warning">
                <SMRegular>
                    {t('user_communication_history.ambiguous_hint')}
                </SMRegular>
                <Box component="ul" mt={1} mb={0} pl={3}>
                    {(error.candidates ?? []).map(
                        (candidate: UserLookupCandidate) => (
                            <li key={candidate.userId}>
                                <SMRegular>
                                    {candidate.displayName ||
                                        t(
                                            'user_communication_history.candidate_fallback_name'
                                        )}{' '}
                                    ({candidate.userId})
                                </SMRegular>
                            </li>
                        )
                    )}
                </Box>
            </Alert>
        );
    }

    let messageKey = 'user_communication_history.error';
    if (error?.code === USER_LOOKUP_ERROR_CODE.NOT_FOUND) {
        messageKey = 'user_communication_history.not_found';
    } else if (error?.code === USER_LOOKUP_ERROR_CODE.MALFORMED) {
        messageKey = 'user_communication_history.malformed';
    }

    return <Alert severity="error">{t(messageKey)}</Alert>;
};
